import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useTheme } from '@mui/material/styles';
import {
  WalteroButton,
  WalteroCard,
  WalteroTypography
} from './waltero';
import billingService from '../services/billingService';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';

/**
 * Card Input Form Component
 *
 * Handles the actual Stripe Elements card input and form submission
 */
const CardInputForm = ({ organizationId, onSuccess, onClose }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [setupIntent, setSetupIntent] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  // Create setup intent on mount
  useEffect(() => {
    createSetupIntent();
  }, []);

  const createSetupIntent = async () => {
    try {
      const result = await billingService.createSetupIntent(organizationId);
      setSetupIntent({
        clientSecret: result.clientSecret,
        customerId: result.customerId
      });
    } catch (err) {
      console.error('Error creating setup intent:', err);
      setError(err.error || err.message || 'Failed to initialize payment setup');
    }
  };

  // Handle card element change
  const handleCardChange = (event) => {
    setError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !setupIntent) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the setup intent with Stripe
      const { error: stripeError, setupIntent: confirmedIntent } = await stripe.confirmCardSetup(
        setupIntent.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)
          }
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Save the payment method to our backend
      await billingService.savePaymentMethod(
        organizationId,
        setupIntent.customerId,
        confirmedIntent.payment_method,
        setAsDefault
      );

      onSuccess();
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(err.message || 'Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  };

  // Stripe Elements styling to match Waltero theme
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        '::placeholder': {
          color: theme.palette.text.secondary
        },
        iconColor: theme.palette.waltero.primary.coreBlue
      },
      invalid: {
        color: theme.palette.error.main,
        iconColor: theme.palette.error.main
      }
    },
    hidePostalCode: false // Show postal code for better fraud prevention
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        {/* Card Icon and Title */}
        <Box display="flex" alignItems="center" mb={3}>
          <CreditCardIcon
            sx={{
              fontSize: 32,
              color: theme.palette.waltero.primary.coreBlue,
              mr: 2
            }}
          />
          <Box>
            <WalteroTypography variant="h6">
              Add Payment Method
            </WalteroTypography>
            <Typography variant="body2" color="textSecondary">
              Enter your card details securely
            </Typography>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Card Input */}
        <WalteroCard
          sx={{
            p: 3,
            mb: 3,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Card Information
          </Typography>
          <Box mt={2}>
            <CardElement
              options={cardElementOptions}
              onChange={handleCardChange}
            />
          </Box>
        </WalteroCard>

        {/* Security Notice */}
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2">
            🔒 Your payment information is encrypted and securely processed by Stripe.
            We never store your card details on our servers.
          </Typography>
        </Alert>

        {/* Set as Default Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={setAsDefault}
              onChange={(e) => setSetAsDefault(e.target.checked)}
              color="primary"
            />
          }
          label="Set as default payment method"
        />

        <Divider sx={{ my: 2 }} />

        {/* Test Card Information (for development) */}
        {process.env.NODE_ENV === 'development' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Test Cards:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              • Success: 4242 4242 4242 4242<br />
              • Requires Auth: 4000 0025 0000 3155<br />
              • Declined: 4000 0000 0000 9995<br />
              Use any future date and any 3-digit CVC
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <WalteroButton
          onClick={onClose}
          disabled={processing}
        >
          CANCEL
        </WalteroButton>
        <WalteroButton
          type="submit"
          variant="contained"
          disabled={!stripe || processing || !cardComplete || !setupIntent}
          startIcon={processing && <CircularProgress size={16} />}
        >
          {processing ? 'ADDING...' : 'ADD PAYMENT METHOD'}
        </WalteroButton>
      </DialogActions>
    </form>
  );
};

/**
 * Add Payment Method Modal
 *
 * Modal wrapper that provides Stripe Elements context
 */
const AddPaymentMethodModal = ({ open, onClose, organizationId, onSuccess }) => {
  const [stripePromise, setStripePromise] = useState(null);

  // Load Stripe on modal open
  useEffect(() => {
    if (open && !stripePromise) {
      billingService.getStripe().then(setStripePromise);
    }
  }, [open, stripePromise]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Add Payment Method
        </Typography>
      </DialogTitle>

      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <CardInputForm
            organizationId={organizationId}
            onSuccess={onSuccess}
            onClose={onClose}
          />
        </Elements>
      ) : (
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default AddPaymentMethodModal;